/**
 * Signed-out marketing view for /organizations (B2B organizations).
 */

import { useCallback, useContext, useMemo, type ReactNode } from 'react'
import type { ComponentType } from 'react'
import { View, Text, ScrollView, Pressable, Platform, useWindowDimensions } from 'react-native'
import { useRouter, type Href } from 'expo-router'
import { ArrowRight, Building2, Palette, Users, Sparkles } from 'lucide-react-native'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../shared/constants/animations'
import { LanguageContext } from '../../../i18n'
import { useTheme } from '../../../theme/useTheme'
import { getAccountAdminPublicMarketingStyles } from './AccountAdminPublicMarketing.styles'
import { B2bContactForm } from '../B2bContactForm/B2bContactForm'
import { MarketingPageFooter } from '../../../../components/landing/MarketingPageFooter/MarketingPageFooter'

export interface AccountAdminPublicMarketingProps {
  signInHref: string
  signUpHref: string
  /** Same sticky nav as the home page (Organizations / Features / Contact). */
  headerSlot?: ReactNode
}

export function AccountAdminPublicMarketing({
  signInHref,
  signUpHref,
  headerSlot,
}: AccountAdminPublicMarketingProps) {
  const router = useRouter()
  const theme = useTheme()
  const { width } = useWindowDimensions()
  const isStackedHero = width > 0 && width < 980
  const styles = useMemo(() => getAccountAdminPublicMarketingStyles(theme), [theme])
  const languageContext = useContext(LanguageContext)
  if (!languageContext) {
    throw new Error('AccountAdminPublicMarketing must be used within LanguageProvider')
  }
  const { t } = languageContext
  const c = t.organizations

  const valueItems = useMemo<
    ReadonlyArray<{
      key: string
      title: string
      body: string
      Icon: ComponentType<{ size: number; color: string }>
    }>
  >(
    () => [
      { key: 'branding', title: c.valueBrandingTitle, body: c.valueBrandingBody, Icon: Palette },
      { key: 'members', title: c.valueMembersTitle, body: c.valueMembersBody, Icon: Users },
      { key: 'mascot', title: c.valueMascotTitle, body: c.valueMascotBody, Icon: Sparkles },
    ],
    [c],
  )

  const goSignIn = useCallback(() => {
    router.push(signInHref as Href)
  }, [router, signInHref])

  const goSignUp = useCallback(() => {
    router.push(signUpHref as Href)
  }, [router, signUpHref])

  const howSteps = [c.howStep1, c.howStep2, c.howStep3] as const

  return (
    <ScrollView
      style={styles.scroll}
      contentContainerStyle={styles.scrollContent}
      keyboardShouldPersistTaps="handled"
    >
      {headerSlot}
      <View style={styles.heroBand}>
        <View style={[styles.bgBlob, styles.bgBlobPrimary]} />
        <View style={[styles.bgBlob, styles.bgBlobGold]} />
        <View style={[styles.heroShell, isStackedHero && styles.heroShellStacked]}>
          <View style={[styles.heroCopy, isStackedHero && styles.heroCopyStacked]}>
            <View style={styles.heroBadge}>
              <Text style={styles.heroBadgeText}>{c.heroBadge}</Text>
            </View>
            <Text style={[styles.heroTitle, isStackedHero && styles.heroTitleStacked]}>
              {c.heroTitle}
            </Text>
            <Text style={[styles.heroLead, isStackedHero && styles.heroLeadStacked]}>
              {c.heroLead}
            </Text>
            <View style={[styles.heroActions, isStackedHero && styles.heroActionsStacked]}>
              <Pressable
                accessibilityRole="button"
                onPress={goSignUp}
                style={({ pressed, hovered }) => [
                  styles.primaryBtn,
                  Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                  pressed && styles.primaryBtnPressed,
                  hovered && Platform.OS === 'web' && styles.primaryBtnHovered,
                ]}
              >
                <Text style={styles.primaryBtnText}>{c.signUp}</Text>
                <ArrowRight size={17} color={theme.white} />
              </Pressable>
              <Pressable
                accessibilityRole="button"
                onPress={goSignIn}
                style={({ pressed, hovered }) => [
                  styles.secondaryBtn,
                  Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                  pressed && styles.secondaryBtnPressed,
                  hovered && Platform.OS === 'web' && styles.secondaryBtnHovered,
                ]}
              >
                <Text style={styles.secondaryBtnText}>{c.signIn}</Text>
              </Pressable>
            </View>
          </View>
          <View style={[styles.heroPanel, isStackedHero && styles.heroPanelStacked]}>
            <View style={styles.heroPanelTopline} />
            <View style={styles.heroPanelIcon}>
              <Building2 size={28} color={theme.primaryDark} />
            </View>
            <Text style={styles.heroPanelTitle}>{c.heroPanelTitle}</Text>
            <Text style={styles.heroPanelBody}>{c.heroPanelBody}</Text>
          </View>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{c.valueSectionTitle}</Text>
        <View style={styles.valueGrid}>
          {valueItems.map(({ key, title, body, Icon }) => (
            <View key={key} style={styles.valueCard}>
              <View style={styles.valueIconWrap}>
                <Icon size={22} color={theme.primary} />
              </View>
              <Text style={styles.valueCardTitle}>{title}</Text>
              <Text style={styles.valueCardBody}>{body}</Text>
            </View>
          ))}
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{c.howSectionTitle}</Text>
        <View style={styles.howList}>
          {howSteps.map((text, i) => (
            <View key={String(i)} style={styles.howRow}>
              <View style={styles.howIndex}>
                <Text style={styles.howIndexText}>{String(i + 1)}</Text>
              </View>
              <Text style={styles.howText}>{text}</Text>
            </View>
          ))}
        </View>
      </View>

      <View style={styles.section}>
        <B2bContactForm />
      </View>

      <MarketingPageFooter contactMode="route" />
    </ScrollView>
  )
}
